/**
 * Basemap registry for the MapLibre adapter.
 *
 * Every entry is a self-contained MapLibre style fragment (sources +
 * layers) so we can swap the active basemap at runtime without touching
 * the user's data layers (the data layers always sit above whatever
 * background is mounted).
 *
 * All entries here use providers that work without an API key on
 * localhost so the viewer is usable in dev / defence-air-gapped without
 * paid tile keys. New ArcGIS Online–style options are wired through the
 * existing `/basemap-esri` (and Google/Carto) Vite proxies.
 */

import type { Map as MLMap, RasterSourceSpecification, LayerSpecification } from 'maplibre-gl';
import type { IconName } from '@/design';
import {
  getCustomBasemap,
  isCustomBasemapId,
  loadCustomBasemaps,
  type CustomBasemapId,
  type CustomBasemapRecord,
} from '@/engines/maplibre/customBasemaps';

export type BuiltInBasemapId =
  | 'none'
  | 'osm'
  | 'esri-imagery'
  | 'google-satellite'
  | 'carto-positron'
  | 'carto-dark-matter'
  | 'opentopomap'
  // ArcGIS Online–style gallery (raster tiles via existing proxies)
  | 'imagery-hybrid'
  | 'streets'
  | 'topographic'
  | 'navigation'
  | 'streets-night'
  | 'terrain-labels'
  | 'light-gray-canvas'
  | 'dark-gray-canvas'
  | 'outdoor'
  | 'oceans'
  | 'natgeo'
  | 'osm-style'
  | 'charted-territory'
  | 'community'
  | 'navigation-dark'
  | 'newspaper'
  | 'human-geography';

/** Built-in gallery id, or a user-defined `custom:<uuid>` XYZ/WMTS entry. */
export type BasemapId = BuiltInBasemapId | CustomBasemapId;

export interface BasemapDefinition {
  id: BasemapId;
  label: string;
  description: string;
  /**
   * Tokenized thumbnail glyph for the basemap picker (no third-party tile
   * imagery in chrome per `ICON_SYSTEM_PLAYBOOK.md` "Brand product icons":
   * monochrome Material Symbols only). Resolved through the `<Icon>`
   * primitive so the color follows the surface ink token.
   */
  thumbnailIcon: IconName;
  /** Mapbox-style source specs keyed by source id. */
  sources: Record<string, RasterSourceSpecification>;
  /** Layers to inject above the canvas background. */
  layers: LayerSpecification[];
}

const BASEMAP_PREFIX = 'basemap';

/** Same-origin basemap tile base (Vite proxies in vite.config.ts). */
function basemapOrigin(): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return '';
}

function esriTile(servicePath: string): string {
  // Esri tile template is {z}/{y}/{x} (not XYZ).
  return `${basemapOrigin()}/basemap-esri/ArcGIS/rest/services/${servicePath}/MapServer/tile/{z}/{y}/{x}`;
}

function cartoTile(stylePath: string): string {
  return `${basemapOrigin()}/basemap-carto/${stylePath}/{z}/{x}/{y}.png`;
}

function googleTile(lyrs: string): string {
  return `${basemapOrigin()}/basemap-google/vt/lyrs=${lyrs}&x={x}&y={y}&z={z}`;
}

interface BasemapLayerSpec {
  /** Stable suffix for source/layer ids (e.g. `base`, `labels`). */
  key: string;
  tiles: string[];
  attribution: string;
  maxzoom?: number;
  opacity?: number;
}

function buildBasemap(
  id: BasemapId,
  label: string,
  description: string,
  thumbnailIcon: IconName,
  layerSpecs: BasemapLayerSpec[],
): BasemapDefinition {
  const sources: Record<string, RasterSourceSpecification> = {};
  const layers: LayerSpecification[] = [];
  for (const spec of layerSpecs) {
    const sourceId = `${BASEMAP_PREFIX}-${id}-${spec.key}-src`;
    const layerId = `${BASEMAP_PREFIX}-${id}-${spec.key}-layer`;
    sources[sourceId] = {
      type: 'raster',
      tiles: spec.tiles,
      tileSize: 256,
      attribution: spec.attribution,
      maxzoom: spec.maxzoom ?? 19,
    };
    layers.push({
      id: layerId,
      type: 'raster',
      source: sourceId,
      paint: { 'raster-opacity': spec.opacity ?? 1 },
    });
  }
  return { id, label, description, thumbnailIcon, sources, layers };
}

function buildXyzBasemap(
  id: BasemapId,
  label: string,
  description: string,
  tileUrls: string[],
  attribution: string,
  thumbnailIcon: IconName,
  maxzoom = 19,
): BasemapDefinition {
  return buildBasemap(id, label, description, thumbnailIcon, [
    { key: 'base', tiles: tileUrls, attribution, maxzoom },
  ]);
}

const ESRI_ATTR =
  'Tiles © Esri — Source: Esri, HERE, Garmin, USGS, NGA, NASA, CGIAR, NCEAS, NLS, OS, NMA, Geodatastyrelsen, Rijkswaterstaat, GSA, Geoland, FEMA, Intermap and the GIS user community';
const ESRI_IMAGERY_ATTR =
  'Tiles © Esri — Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community';
const CARTO_ATTR = '© OpenStreetMap contributors © CARTO';
const OSM_ATTR = '© OpenStreetMap contributors';
const GOOGLE_ATTR = 'Imagery © Google';

export const BASEMAPS: BasemapDefinition[] = [
  {
    id: 'none',
    label: 'No basemap',
    description: 'Transparent background; only your data layers are rendered.',
    thumbnailIcon: 'visibility_off',
    sources: {},
    layers: [],
  },
  buildXyzBasemap(
    'osm',
    'OpenStreetMap',
    'OpenStreetMap standard street map (via local Vite proxy).',
    [`${basemapOrigin()}/basemap-osm/{z}/{x}/{y}.png`],
    OSM_ATTR,
    'map',
    19,
  ),
  buildXyzBasemap(
    'esri-imagery',
    'Esri World Imagery',
    'High-resolution satellite imagery from Esri (via local Vite proxy).',
    [esriTile('World_Imagery')],
    ESRI_IMAGERY_ATTR,
    'public',
    19,
  ),
  buildXyzBasemap(
    'google-satellite',
    'Google Satellite',
    'Google satellite imagery (same XYZ template as QGIS: mt1.google.com lyrs=s).',
    [googleTile('s')],
    GOOGLE_ATTR,
    'public',
    20,
  ),
  buildXyzBasemap(
    'carto-positron',
    'CartoDB Positron (light)',
    'Minimal light cartographic style ideal for raster overlays.',
    [cartoTile('light_all')],
    CARTO_ATTR,
    'wb_sunny',
    19,
  ),
  buildXyzBasemap(
    'carto-dark-matter',
    'CartoDB Dark Matter',
    'Dark cartographic style ideal for night and high-contrast overlays.',
    [cartoTile('dark_all')],
    CARTO_ATTR,
    'wb_shade',
    19,
  ),
  buildXyzBasemap(
    'opentopomap',
    'OpenTopoMap',
    'Topographic style with contours and shaded relief (Esri World Topo).',
    [esriTile('World_Topo_Map')],
    ESRI_ATTR,
    'landscape',
    19,
  ),

  // --- ArcGIS Online–style gallery -----------------------------------------

  buildBasemap(
    'imagery-hybrid',
    'Imagery Hybrid',
    'Satellite imagery with roads and place labels overlaid.',
    'public',
    [
      {
        key: 'base',
        tiles: [esriTile('World_Imagery')],
        attribution: ESRI_IMAGERY_ATTR,
        maxzoom: 19,
      },
      {
        key: 'labels',
        tiles: [esriTile('Reference/World_Boundaries_and_Places')],
        attribution: ESRI_ATTR,
        maxzoom: 19,
      },
    ],
  ),
  buildXyzBasemap(
    'streets',
    'Streets',
    'Detailed worldwide street map with highways, roads, and landmarks.',
    [esriTile('World_Street_Map')],
    ESRI_ATTR,
    'directions',
    19,
  ),
  buildXyzBasemap(
    'topographic',
    'Topographic',
    'Reference topographic map with contours, land cover, and shaded relief.',
    [esriTile('World_Topo_Map')],
    ESRI_ATTR,
    'terrain',
    19,
  ),
  buildXyzBasemap(
    'navigation',
    'Navigation',
    'Road-focused navigation map optimised for wayfinding.',
    [googleTile('m')],
    GOOGLE_ATTR,
    'directions',
    20,
  ),
  buildBasemap(
    'streets-night',
    'Streets (Night)',
    'Dark street canvas with reference labels for night-time viewing.',
    'dark_mode',
    [
      {
        key: 'base',
        tiles: [esriTile('Canvas/World_Dark_Gray_Base')],
        attribution: ESRI_ATTR,
        maxzoom: 16,
      },
      {
        key: 'labels',
        tiles: [esriTile('Canvas/World_Dark_Gray_Reference')],
        attribution: ESRI_ATTR,
        maxzoom: 16,
      },
    ],
  ),
  buildBasemap(
    'terrain-labels',
    'Terrain with Labels',
    'Shaded relief terrain with place and feature labels.',
    'landscape',
    [
      {
        key: 'base',
        tiles: [esriTile('World_Terrain_Base')],
        attribution: ESRI_ATTR,
        maxzoom: 13,
      },
      {
        key: 'labels',
        tiles: [esriTile('Reference/World_Reference_Overlay')],
        attribution: ESRI_ATTR,
        maxzoom: 13,
      },
    ],
  ),
  buildBasemap(
    'light-gray-canvas',
    'Light Gray Canvas',
    'Neutral light canvas that keeps thematic overlays in the foreground.',
    'wb_sunny',
    [
      {
        key: 'base',
        tiles: [esriTile('Canvas/World_Light_Gray_Base')],
        attribution: ESRI_ATTR,
        maxzoom: 16,
      },
      {
        key: 'labels',
        tiles: [esriTile('Canvas/World_Light_Gray_Reference')],
        attribution: ESRI_ATTR,
        maxzoom: 16,
      },
    ],
  ),
  buildBasemap(
    'dark-gray-canvas',
    'Dark Gray Canvas',
    'Neutral dark canvas for high-contrast thematic overlays.',
    'wb_shade',
    [
      {
        key: 'base',
        tiles: [esriTile('Canvas/World_Dark_Gray_Base')],
        attribution: ESRI_ATTR,
        maxzoom: 16,
      },
      {
        key: 'labels',
        tiles: [esriTile('Canvas/World_Dark_Gray_Reference')],
        attribution: ESRI_ATTR,
        maxzoom: 16,
      },
    ],
  ),
  buildBasemap(
    'outdoor',
    'Outdoor',
    'Terrain and reference overlay suited to outdoor / field context.',
    'terrain',
    [
      {
        key: 'base',
        tiles: [esriTile('World_Terrain_Base')],
        attribution: ESRI_ATTR,
        maxzoom: 13,
      },
      {
        key: 'labels',
        tiles: [esriTile('Reference/World_Reference_Overlay')],
        attribution: ESRI_ATTR,
        maxzoom: 13,
      },
    ],
  ),
  buildBasemap(
    'oceans',
    'Oceans',
    'Ocean basemap with bathymetry and coastal reference labels.',
    'waves',
    [
      {
        key: 'base',
        tiles: [esriTile('Ocean/World_Ocean_Base')],
        attribution: ESRI_ATTR,
        maxzoom: 16,
      },
      {
        key: 'labels',
        tiles: [esriTile('Ocean/World_Ocean_Reference')],
        attribution: ESRI_ATTR,
        maxzoom: 16,
      },
    ],
  ),
  buildXyzBasemap(
    'natgeo',
    'National Geographic Style Map',
    'Classic National Geographic cartographic style.',
    [esriTile('NatGeo_World_Map')],
    ESRI_ATTR,
    'public',
    16,
  ),
  buildXyzBasemap(
    'osm-style',
    'OpenStreetMap Style',
    'OpenStreetMap community style (same tiles as OpenStreetMap).',
    [`${basemapOrigin()}/basemap-osm/{z}/{x}/{y}.png`],
    OSM_ATTR,
    'map',
    19,
  ),
  buildXyzBasemap(
    'charted-territory',
    'Charted Territory Map',
    'Atlas-style charted territory map (Esri World Physical).',
    [esriTile('World_Physical_Map')],
    ESRI_ATTR,
    'map',
    8,
  ),
  buildXyzBasemap(
    'community',
    'Community Map',
    'Community-oriented street style (Carto Voyager).',
    [cartoTile('rastertiles/voyager')],
    CARTO_ATTR,
    'person_pin_circle',
    19,
  ),
  buildXyzBasemap(
    'navigation-dark',
    'Navigation (Dark)',
    'Dark navigation-friendly street style.',
    [cartoTile('dark_all')],
    CARTO_ATTR,
    'dark_mode',
    19,
  ),
  buildBasemap(
    'newspaper',
    'Newspaper Map',
    'Muted light-gray canvas reminiscent of print / newspaper maps.',
    'crop_landscape',
    [
      {
        key: 'base',
        tiles: [esriTile('Canvas/World_Light_Gray_Base')],
        attribution: ESRI_ATTR,
        maxzoom: 16,
      },
      {
        key: 'labels',
        tiles: [esriTile('Canvas/World_Light_Gray_Reference')],
        attribution: ESRI_ATTR,
        maxzoom: 16,
        opacity: 0.85,
      },
    ],
  ),
  buildXyzBasemap(
    'human-geography',
    'Human Geography',
    'Physical / human-geography style basemap with land cover context.',
    [esriTile('World_Physical_Map')],
    ESRI_ATTR,
    'public',
    8,
  ),
];

const BASEMAP_BY_ID = new Map(BASEMAPS.map((b) => [b.id, b]));

// Light Carto via same-origin proxy — readable on first open (dark+dark
// made empty/failed maps look identical to a working dark basemap).
export const DEFAULT_BASEMAP_ID: BasemapId = 'carto-positron';
const STORAGE_KEY = 'geoviewer.basemap';

function customRecordToDefinition(record: CustomBasemapRecord): BasemapDefinition {
  const id = `custom:${record.id}` as CustomBasemapId;
  const protocolLabel = record.protocol === 'wmts' ? 'WMTS' : 'XYZ';
  return buildXyzBasemap(
    id,
    record.label,
    `Custom ${protocolLabel} basemap.`,
    [record.urlTemplate],
    record.attribution,
    'layers',
    record.maxZoom,
  );
}

/** Built-in gallery plus any user-defined XYZ/WMTS basemaps. */
export function getAllBasemaps(): BasemapDefinition[] {
  return [
    ...BASEMAPS,
    ...loadCustomBasemaps().map(customRecordToDefinition),
  ];
}

export function getBasemap(id: BasemapId): BasemapDefinition {
  if (isCustomBasemapId(id)) {
    const record = getCustomBasemap(id);
    if (record) return customRecordToDefinition(record);
    return BASEMAP_BY_ID.get(DEFAULT_BASEMAP_ID)!;
  }
  return BASEMAP_BY_ID.get(id) ?? BASEMAP_BY_ID.get(DEFAULT_BASEMAP_ID)!;
}

export function loadPreferredBasemapId(): BasemapId {
  if (typeof window === 'undefined') return DEFAULT_BASEMAP_ID;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    // One-time migration: older sessions defaulted to dark-matter which
    // looked "blank" when COG tiles were missing. Prefer light streets.
    if (v === 'carto-dark-matter') {
      window.localStorage.setItem(STORAGE_KEY, DEFAULT_BASEMAP_ID);
      return DEFAULT_BASEMAP_ID;
    }
    if (v && isCustomBasemapId(v) && getCustomBasemap(v)) {
      return v;
    }
    if (v && BASEMAP_BY_ID.has(v as BuiltInBasemapId)) return v as BasemapId;
  } catch {
    /* ignore storage failures (private mode, etc) */
  }
  return DEFAULT_BASEMAP_ID;
}

export function savePreferredBasemapId(id: BasemapId): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, id);
  } catch {
    /* ignore */
  }
}

/**
 * Ordered tile URL templates for a basemap (base first, then overlays).
 * Used by the Canvas / OpenLayers adapter which paints one OL layer per URL.
 */
export function basemapTileUrls(id: BasemapId): string[] {
  const def = getBasemap(id);
  const urls: string[] = [];
  for (const layer of def.layers) {
    if (layer.type !== 'raster' || typeof layer.source !== 'string') continue;
    const source = def.sources[layer.source];
    const tiles = source && 'tiles' in source ? source.tiles : undefined;
    if (Array.isArray(tiles) && typeof tiles[0] === 'string') {
      urls.push(tiles[0]);
    }
  }
  return urls;
}

/**
 * Concrete sample tiles for visual basemap cards only.
 *
 * Uses central Dubai so previews match the project's primary region.
 * These URLs are for gallery `<img>` thumbnails and never touch the live
 * map tile pipeline, so opening the picker cannot slow map panning/zoom.
 */
export function basemapPreviewTileUrls(id: BasemapId): string[] {
  // Downtown Dubai — dense roads, coastline, and landmarks for every style.
  const previewLon = 55.2708;
  const previewLat = 25.2048;
  const def = getBasemap(id);
  const urls: string[] = [];

  for (const layer of def.layers) {
    if (layer.type !== 'raster' || typeof layer.source !== 'string') continue;
    const source = def.sources[layer.source];
    const templates = source && 'tiles' in source ? source.tiles : undefined;
    if (!Array.isArray(templates) || typeof templates[0] !== 'string') continue;

    const z = Math.min(source.maxzoom ?? 10, 10);
    const scale = 2 ** z;
    const x = Math.floor(((previewLon + 180) / 360) * scale);
    const latRadians = (previewLat * Math.PI) / 180;
    const y = Math.floor(
      ((1 - Math.asinh(Math.tan(latRadians)) / Math.PI) / 2) * scale,
    );

    urls.push(
      templates[0]
        .replaceAll('{z}', String(z))
        .replaceAll('{x}', String(x))
        .replaceAll('{y}', String(y)),
    );
  }

  return urls;
}

/**
 * Remove every basemap source / layer registered through this module from
 * the given map. Safe to call repeatedly. Used before swapping basemaps.
 */
function removeAllBasemaps(map: MLMap): void {
  const style = map.getStyle();
  if (!style) return;
  for (const layer of style.layers ?? []) {
    if (layer.id.startsWith(BASEMAP_PREFIX)) {
      try {
        map.removeLayer(layer.id);
      } catch {
        /* ignore */
      }
    }
  }
  for (const sourceId of Object.keys(style.sources ?? {})) {
    if (sourceId.startsWith(BASEMAP_PREFIX)) {
      try {
        map.removeSource(sourceId);
      } catch {
        /* ignore */
      }
    }
  }
}

/**
 * Apply a basemap to a mounted MapLibre map. Inserts new sources/layers
 * directly above the `background` style layer so user data layers stay
 * on top.
 */
export function applyBasemap(map: MLMap, id: BasemapId): void {
  const def = getBasemap(id);
  removeAllBasemaps(map);
  for (const [sourceId, spec] of Object.entries(def.sources)) {
    map.addSource(sourceId, spec);
  }
  // Find the first non-basemap, non-background layer to insert before so
  // basemaps stay below user data; null = insert at the top of the stack.
  const style = map.getStyle();
  const beforeId = (style.layers ?? []).find(
    (l) => l.id !== 'background' && !l.id.startsWith(BASEMAP_PREFIX),
  )?.id;
  for (const layer of def.layers) {
    map.addLayer(layer, beforeId);
  }
}
